import { useNavigate } from 'react-router-dom';
import { useEffect } from 'react';
import Button from '../components/common/Button';
import useApplicationStore from '../stores/applicationStore';
import useAuthStore from '../stores/authStore';

const ApplicationStatus = () => {
  const navigate = useNavigate();
  const { currentApplication, clearCurrentApplication } = useApplicationStore();
  const { profile } = useAuthStore();

  useEffect(() => {
    // Redirect if no current application
    if (!currentApplication) {
      navigate('/dashboard');
    }
  }, [currentApplication, navigate]);

  const handleViewApplications = () => {
    navigate('/dashboard');
  };

  const handleApplyAgain = () => {
    // Clear current application to start fresh
    clearCurrentApplication();
    navigate('/service-fee-selection');
  };

  const formatApplicationId = (id) => {
    // Format application ID for display
    return id ? id.slice(-8).toUpperCase() : 'N/A';
  };

  const formatDate = (timestamp) => {
    let date;
    
    // Handle Firestore Timestamp objects
    if (timestamp && typeof timestamp.toDate === 'function') {
      date = timestamp.toDate();
    } 
    // Handle regular Date objects or ISO strings
    else if (timestamp) {
      date = new Date(timestamp);
    } 
    // Handle invalid/missing timestamps
    else {
      return 'N/A';
    }
    
    // Check if date is valid
    if (isNaN(date.getTime())) {
      return 'N/A';
    }
    
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Show loading if no application data
  if (!currentApplication) {
    return (
      <div className="page-container">
        <div className="form-container">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading application status...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="form-container">
        <div className="text-center mb-8">
          <div className="w-20 h-20 bg-success-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-10 h-10 text-success-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">
            Application Submitted!
          </h1>
          <p className="text-gray-600">
            Your loan application has been successfully submitted
          </p>
        </div>

        {/* User Context */}
        <div className="bg-gray-50 rounded-xl p-4 mb-6">
          <div className="flex items-center">
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center mr-3">
              <span className="text-primary-600 font-semibold">
                {profile?.fullName?.charAt(0)?.toUpperCase()}
              </span>
            </div>
            <div>
              <p className="font-medium text-gray-900">{profile?.fullName}</p>
              <p className="text-sm text-gray-600">{profile?.email}</p>
            </div>
          </div>
        </div>

        <div className="bg-primary-50 rounded-xl p-6 mb-6">
          <h2 className="text-lg font-semibold text-primary-900 mb-4">
            Application Summary
          </h2>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Application ID:</span>
              <span className="font-mono font-semibold text-primary-900">
                {formatApplicationId(currentApplication.id)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Loan Amount:</span>
              <span className="font-semibold text-primary-900">
                KES {currentApplication.eligibleLoanAmount?.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Service Fee Paid:</span>
              <span className="font-semibold text-primary-900">
                KES {currentApplication.serviceAmount}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Loan Purpose:</span>
              <span className="text-primary-900">
                {currentApplication.loanPurpose}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">M-Pesa Number:</span>
              <span className="font-mono text-sm text-primary-900">
                {currentApplication.mpesaNumber}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Applied:</span>
              <span className="text-sm text-primary-900">
                {formatDate(currentApplication.appliedAt)}
              </span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-primary-700">Status:</span>
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                Pending Review
              </span>
            </div>
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center mr-3 mt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h3 className="font-semibold text-blue-900 mb-2">What happens next?</h3>
              <ul className="text-blue-800 text-sm space-y-2">
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2"></span>
                  Your application will be reviewed within <strong>48 hours</strong>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2"></span>
                  If approved, funds will be sent directly to <strong>{currentApplication.mpesaNumber}</strong>
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2"></span>
                  You'll receive an SMS notification with the status update
                </li>
                <li className="flex items-start">
                  <span className="inline-block w-1.5 h-1.5 bg-blue-600 rounded-full mt-2 mr-2"></span>
                  You can check your application status anytime in your dashboard
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <Button
            type="button"
            size="lg"
            className="w-full"
            onClick={handleViewApplications}
          >
            View My Applications
          </Button>
          
          <Button
            type="button"
            variant="outline"
            size="lg"
            className="w-full"
            onClick={handleApplyAgain}
          >
            Apply for Another Loan
          </Button>
        </div>

        <div className="mt-6 p-4 bg-green-50 rounded-lg">
          <div className="flex items-start">
            <svg className="w-5 h-5 text-green-500 mr-2 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-medium text-green-800">
                Application Reference Saved
              </p>
              <p className="text-sm text-green-700 mt-1">
                Your application ID <strong>{formatApplicationId(currentApplication.id)}</strong> has been saved to your account for future reference.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ApplicationStatus;